import { formatProductSize } from '~/utils/format-product-size';
import { useEffect, useState } from 'react';
import { hasStrain, productSizeFamily, toProductTitleCase } from '~/utils/product-strain';

type SizeProduct = { id: string; name: string; size: string; subtype?: string };
type Props = {
  product: SizeProduct & { brand: string; strain?: string; category: string };
  store: string;
  onSelect: (id: string) => void;
};

export default function StrainSizes({ product, store, onSelect }: Props) {
  const [result, setResult] = useState<{ key: string; products: SizeProduct[] }>();
  const { brand, strain, category, name, subtype } = product;
  const key = JSON.stringify([store, brand, strain, category, name, subtype]);
  useEffect(() => {
    if ((!hasStrain(strain) && !productSizeFamily(name)) || !store) return;
    const controller = new AbortController();
    const params = new URLSearchParams({ store, brand, strain: strain || '', category, name, subtype: subtype || '' });
    fetch(`/api/product-sizes?${params}`, { signal: controller.signal })
      .then((response) => (response.ok ? response.json() : { products: [] }))
      .then((data) => {
        if (!controller.signal.aborted) setResult({ key, products: data.products || [] });
      })
      .catch(() => {});
    return () => controller.abort();
  }, [key, store, brand, strain, category, name, subtype]);
  const matches = result?.key === key ? result.products : [];
  const products = matches.some((p) => p.id === product.id) ? matches : [product, ...matches];
  const differentFormats = new Set(products.map((p) => p.subtype).filter(Boolean)).size > 1;
  if (!product.size && !matches.length) return null;
  return (
    <div className="mb-6 flex flex-col gap-2">
      <span className="font-helvetica-neue text-xs font-medium tracking-wide text-gray-600 uppercase">
        Size
      </span>
      <div className="flex flex-wrap gap-2" role="group" aria-label="Available sizes">
        {products.map((p) => (
          <button
            key={p.id}
            type="button"
            aria-pressed={p.id === product.id}
            title={p.name}
            onClick={() => {
              if (p.id !== product.id) onSelect(p.id);
            }}
            className={`font-helvetica-neue min-h-11 rounded-lg border px-4 py-2 text-sm transition-colors hover:border-black focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#264B9A] ${p.id === product.id ? 'border-black bg-[#ffd100] font-semibold shadow-[inset_0_0_0_1px_#000]' : 'border-gray-300 bg-white text-black'}`}
          >
            {formatProductSize(p.size) || 'Current size'}
            {differentFormats && p.subtype && (
              <small className="mt-1 block text-xs font-normal">
                {toProductTitleCase(p.subtype)}
              </small>
            )}
          </button>
        ))}
      </div>
    </div>
  );
}
